import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { DadosInstalacao, DadosIrradiacao, tiposEstrutura, nomesEstrutura } from "@/types";
import { ArrowLeft, BarChart } from "lucide-react";
import { concessionarias } from "@/types";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { getSistemaPadraoConfig } from "./utils/kitSolarCalculations";

interface DadosInstalacaoFormProps {
  onDadosSalvos: (dados: DadosInstalacao) => void;
  dadosIniciais?: DadosInstalacao;
  onVoltar?: () => void;
  dadosIrradiacao?: DadosIrradiacao;
}

const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", 
               "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function gerarDadosConsumoMensalPadrao(valorMedio: number) {
  return meses.map((mes) => ({
    mes,
    valor: valorMedio
  }));
}

export const DadosInstalacaoForm = ({
  onDadosSalvos,
  dadosIniciais,
  onVoltar,
  dadosIrradiacao,
}: DadosInstalacaoFormProps) => {
  const [tipoTensao, setTipoTensao] = useState<"baixa" | "media">(
    dadosIniciais?.tipoTensao || "baixa"
  );
  const [difusaoAzimutal, setDifusaoAzimutal] = useState<number>(
    dadosIniciais?.difusaoAzimutal || 0
  );
  const [inclinacaoTelhado, setInclinacaoTelhado] = useState<number>(
    dadosIniciais?.inclinacaoTelhado || 15
  );
  const [tipologia, setTipologia] = useState<
    "residencial" | "comercial" | "industrial" | "rural"
  >(dadosIniciais?.tipologia || "residencial");
  const [consumoMensal, setConsumoMensal] = useState<number>(
    dadosIniciais?.consumoMensal || 0
  );
  const [consumoHoraPonta, setConsumoHoraPonta] = useState<number>(
    dadosIniciais?.consumoHoraPonta || 0
  );
  const [consumoForaPonta, setConsumoForaPonta] = useState<number>(
    dadosIniciais?.consumoForaPonta || 0
  );
  const [demandaContratada, setDemandaContratada] = useState<number>(
    dadosIniciais?.demandaContratada || 0
  );
  const [areaDeTelhado, setAreaDeTelhado] = useState<number>(
    dadosIniciais?.areaDeTelhado || 0
  );
  const [tipoTelhado, setTipoTelhado] = useState<string>(
    dadosIniciais?.tipoTelhado || ""
  );
  const [concessionaria, setConcessionaria] = useState<string>(
    dadosIniciais?.concessionaria || ""
  );
  const [tipoTarifa, setTipoTarifa] = useState<string>(
    dadosIniciais?.tipoTarifa || ""
  );
  const [consumoMensalDetalhado, setConsumoMensalDetalhado] = useState<boolean>(
    dadosIniciais?.consumoMensalDetalhado || false
  );
  const [dadosConsumoMensal, setDadosConsumoMensal] = useState<{ mes: string; valor: number }[]>(
    dadosIniciais?.dadosConsumoMensal || gerarDadosConsumoMensalPadrao(0)
  );
  const [mostrarGrafico, setMostrarGrafico] = useState<boolean>(false);
  const [tiposEstruturaConfig, setTiposEstruturaConfig] = useState<{tipo: string, nome: string}[]>([]);

  useEffect(() => {
    const loadSavedStructureTypes = () => {
      const tiposEstruturaArray = tiposEstrutura.map(tipo => ({
        tipo,
        nome: nomesEstrutura[tipo] || tipo
      }));
      
      setTiposEstruturaConfig(tiposEstruturaArray);
    };
    
    loadSavedStructureTypes();
  }, []);

  useEffect(() => {
    if (tipoTensao === "media") {
      setConsumoMensal(consumoHoraPonta + consumoForaPonta);
    }
  }, [consumoHoraPonta, consumoForaPonta, tipoTensao]);

  useEffect(() => {
    if (!consumoMensalDetalhado) {
      setDadosConsumoMensal(gerarDadosConsumoMensalPadrao(consumoMensal));
    }
  }, [consumoMensal, consumoMensalDetalhado]);

  useEffect(() => {
    if (consumoMensalDetalhado) {
      const total = dadosConsumoMensal.reduce((acc, item) => acc + item.valor, 0);
      const media = total / 12;
      setConsumoMensal(media);
    }
  }, [dadosConsumoMensal, consumoMensalDetalhado]);

  const getTiposTarifaPorTipologia = () => {
    if (tipoTensao === "media") {
      return [
        { value: "verde", label: "Tarifa Verde (A4)" },
        { value: "azul", label: "Tarifa Azul (A4)" }
      ];
    }
    
    switch (tipologia) {
      case "residencial":
        return [
          { value: "convencional", label: "Convencional" },
          { value: "branca", label: "Tarifa Branca" },
          { value: "social", label: "Tarifa Social" },
        ];
      case "comercial":
        return [
          { value: "verde", label: "Tarifa Verde" },
          { value: "azul", label: "Tarifa Azul" },
          { value: "convencional", label: "Convencional" },
        ];
      case "industrial":
        return [
          { value: "verde", label: "Tarifa Verde" },
          { value: "azul", label: "Tarifa Azul" },
          { value: "convencional", label: "Convencional" },
        ];
      case "rural":
        return [
          { value: "rural", label: "Tarifa Rural" },
          { value: "irrigacao", label: "Irrigação" },
          { value: "convencional", label: "Convencional" },
        ];
      default:
        return [{ value: "convencional", label: "Convencional" }];
    }
  };

  const atualizarConsumoMensal = (index: number, valor: number) => {
    const novosDados = [...dadosConsumoMensal];
    novosDados[index] = { ...novosDados[index], valor };
    setDadosConsumoMensal(novosDados);
  };

  const handleTipologiaChange = (value: "residencial" | "comercial" | "industrial" | "rural") => {
    setTipologia(value);
    setTipoTarifa("");
  };

  const handleTipoTensaoChange = (value: "baixa" | "media") => {
    setTipoTensao(value);
    setTipoTarifa("");
    
    if (value === "baixa") {
      setConsumoHoraPonta(0);
      setConsumoForaPonta(0);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (consumoMensal <= 0) {
      toast.error("Informe o consumo mensal");
      return;
    }

    const dadosInstalacao: DadosInstalacao = {
      tipoTensao,
      difusaoAzimutal,
      inclinacaoTelhado,
      tipologia,
      consumoMensal,
      areaDeTelhado: 0,
      tipoTelhado,
      concessionaria,
      tipoTarifa,
      consumoMensalDetalhado,
      dadosConsumoMensal
    };

    if (tipoTensao === "media") {
      dadosInstalacao.consumoHoraPonta = consumoHoraPonta;
      dadosInstalacao.consumoForaPonta = consumoForaPonta;
      dadosInstalacao.demandaContratada = demandaContratada;
    }

    onDadosSalvos(dadosInstalacao);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Dados da Instalação</CardTitle>
          <CardDescription>
            Informe os detalhes técnicos da instalação solar
          </CardDescription>
        </div>
        {onVoltar && (
          <Button
            variant="outline"
            size="sm"
            onClick={onVoltar}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="concessionaria">Concessionária</Label>
            <Select
              value={concessionaria}
              onValueChange={(value) => setConcessionaria(value)}
            >
              <SelectTrigger id="concessionaria">
                <SelectValue placeholder="Selecione a concessionária" />
              </SelectTrigger>
              <SelectContent>
                {concessionarias.map((conc) => (
                  <SelectItem key={conc} value={conc}>
                    {conc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tipoTensao">Tipo de Tensão</Label>
            <Select
              value={tipoTensao}
              onValueChange={(value) => handleTipoTensaoChange(value as "baixa" | "media")}
            >
              <SelectTrigger id="tipoTensao">
                <SelectValue placeholder="Selecione o tipo de tensão" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="baixa">Baixa Tensão</SelectItem>
                <SelectItem value="media">Média Tensão (A4)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {tipoTensao === "baixa" && (
            <div className="space-y-2">
              <Label htmlFor="tipologia">Tipologia</Label>
              <Select
                value={tipologia}
                onValueChange={(value) =>
                  handleTipologiaChange(value as "residencial" | "comercial" | "industrial" | "rural")
                }
              >
                <SelectTrigger id="tipologia">
                  <SelectValue placeholder="Selecione a tipologia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="residencial">Residencial</SelectItem>
                  <SelectItem value="comercial">Comercial</SelectItem>
                  <SelectItem value="industrial">Industrial</SelectItem>
                  <SelectItem value="rural">Rural</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="tipoTarifa">Tipo de Tarifa</Label>
            <Select
              value={tipoTarifa}
              onValueChange={(value) => setTipoTarifa(value)}
              disabled={!tipologia && tipoTensao === "baixa" || tipoTensao === "media" && !tipoTensao}
            >
              <SelectTrigger id="tipoTarifa">
                <SelectValue placeholder={tipoTensao === "media" ? "Selecione o tipo de tarifa" : 
                                       tipologia ? "Selecione o tipo de tarifa" : 
                                       "Selecione a tipologia primeiro"} />
              </SelectTrigger>
              <SelectContent>
                {getTiposTarifaPorTipologia().map((tarifa) => (
                  <SelectItem key={tarifa.value} value={tarifa.value}>
                    {tarifa.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {tipoTensao === "media" && (
            <div className="bg-green-50 p-4 rounded-lg space-y-4">
              <h3 className="font-medium text-green-800">Dados de Média Tensão (A4)</h3>
              
              <div className="space-y-2">
                <Label htmlFor="consumoHoraPonta">Consumo Hora Ponta (kWh/mês)</Label>
                <Input
                  id="consumoHoraPonta"
                  type="number"
                  value={consumoHoraPonta}
                  onChange={(e) => setConsumoHoraPonta(Number(e.target.value))}
                  placeholder="300"
                  min={0}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="consumoForaPonta">Consumo Fora Ponta (kWh/mês)</Label>
                <Input
                  id="consumoForaPonta"
                  type="number"
                  value={consumoForaPonta}
                  onChange={(e) => setConsumoForaPonta(Number(e.target.value))}
                  placeholder="1500"
                  min={0}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="demandaContratada">Demanda Contratada (kW)</Label>
                <Input
                  id="demandaContratada"
                  type="number"
                  value={demandaContratada}
                  onChange={(e) => setDemandaContratada(Number(e.target.value))}
                  placeholder="30"
                  min={0}
                />
              </div>
              
              {consumoHoraPonta > 0 && consumoForaPonta > 0 && (
                <div className="bg-white p-3 rounded border border-green-200">
                  <p className="text-sm text-gray-600">Consumo total: <span className="font-medium">{(consumoHoraPonta + consumoForaPonta).toLocaleString()} kWh/mês</span></p>
                  <p className="text-sm text-gray-600">Relação ponta/total: <span className="font-medium">{((consumoHoraPonta / (consumoHoraPonta + consumoForaPonta)) * 100).toFixed(1)}%</span></p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label>Difusão Azimutal (graus): {difusaoAzimutal}°</Label>
              <Slider
                value={[difusaoAzimutal]}
                min={0}
                max={360}
                step={1}
                onValueChange={(value) => setDifusaoAzimutal(value[0])}
                className="my-2"
              />
            </div>

            <div>
              <Label>Inclinação do Telhado (graus): {inclinacaoTelhado}°</Label>
              <Slider
                value={[inclinacaoTelhado]}
                min={0}
                max={45}
                step={1}
                onValueChange={(value) => setInclinacaoTelhado(value[0])}
                className="my-2"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tipoTelhado">Tipo de Telhado</Label>
            <Select
              value={tipoTelhado}
              onValueChange={(value) => setTipoTelhado(value)}
            >
              <SelectTrigger id="tipoTelhado">
                <SelectValue placeholder="Selecione o tipo de telhado" />
              </SelectTrigger>
              <SelectContent>
                {tiposEstruturaConfig.map((estrutura) => (
                  <SelectItem key={estrutura.tipo} value={estrutura.tipo}>
                    {estrutura.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {tipoTensao === "baixa" && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="consumo-detalhado"
                  checked={consumoMensalDetalhado}
                  onCheckedChange={setConsumoMensalDetalhado}
                />
                <Label htmlFor="consumo-detalhado">
                  Informar consumo mensal detalhado (mês a mês)
                </Label>
              </div>

              {!consumoMensalDetalhado ? (
                <div className="space-y-2">
                  <Label htmlFor="consumoMensal">Consumo Médio Mensal (kWh)</Label>
                  <Input
                    id="consumoMensal"
                    type="number"
                    value={consumoMensal}
                    onChange={(e) => setConsumoMensal(Number(e.target.value))}
                    placeholder="500"
                    min={0}
                    required
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="text-base font-medium">Consumo Mensal Detalhado (kWh/mês)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {meses.map((mes, index) => (
                      <div key={mes} className="space-y-1">
                        <Label htmlFor={`consumo-${mes}`}>{mes}</Label>
                        <Input
                          id={`consumo-${mes}`}
                          type="number"
                          value={dadosConsumoMensal[index]?.valor || 0}
                          onChange={(e) => atualizarConsumoMensal(index, Number(e.target.value))}
                          placeholder="0"
                          min={0}
                          className="w-full"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-2 p-3 bg-green-50 rounded-md">
                    <p className="text-sm font-medium text-green-800">
                      Consumo médio: {consumoMensal.toFixed(2)} kWh/mês
                    </p>
                    <p className="text-sm text-green-700">
                      Consumo anual: {(consumoMensal * 12).toFixed(2)} kWh/ano
                    </p>
                  </div>
                </div>
              )}
              
              {consumoMensal > 0 && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setMostrarGrafico(!mostrarGrafico)}
                  className="flex items-center gap-2 w-full mt-2"
                >
                  <BarChart className="h-4 w-4" />
                  {mostrarGrafico ? "Ocultar Gráfico" : "Visualizar Gráfico"}
                </Button>
              )}
              
              {mostrarGrafico && consumoMensal > 0 && (
                <div className="mt-4 space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <BarChart className="h-5 w-5 text-green-600" /> 
                    Consumo Mensal (kWh/mês)
                  </h3>
                  <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart data={dadosConsumoMensal.map(item => ({
                        name: item.mes.substring(0, 3),
                        consumo: item.valor
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis domain={[0, 'auto']} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="consumo" fill="#3b82f6" name="Consumo (kWh)" radius={[4, 4, 0, 0]} />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          <Button type="submit" className="w-full">
            Salvar Dados de Instalação
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
